import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMainPageTitle } from "../../contexts/PageContext";
import { useAuth } from "../../contexts/authContext";
import Btn from "../../components/form/btn";

const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";

export default function CompleteProfileGender() {
  const navigate = useNavigate();
  const { updatePageTitle } = useMainPageTitle();
  const { user, updateUser } = useAuth();
  const { t } = useTranslation();
  
  const [selectedGender, setSelectedGender] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const genderOptions = [
    {
      value: "male",
      label: t("labels.male"),
      icon: "👨"
    },
    {
      value: "female",
      label: t("labels.female"),
      icon: "👩"
    }
  ];

  useEffect(() => {
    updatePageTitle("Complete Profile - Gender", "Select your gender");
    
    if (!user) {
      navigate("/signin");
      return;
    }

    // If user doesn't have account type or phone, redirect to appropriate step
    if (!user.accountType || user.accountType.trim() === '') {
      navigate("/complete-profile");
      return;
    }
    if (!user.phone || user.phone.trim() === '') {
      navigate("/complete-profile/phone");
      return;
    }

    // Organizations should not access this page
    if (user.accountType === "organization") {
      navigate("/complete-profile/documents");
      return;
    }

    // Check if user already has a valid gender set (not "other" unless they're an organization)
    const hasValidGender = user.gender && user.gender.trim() !== '' && 
                          (user.gender !== 'other' || user.accountType === 'organization');
    
    if (hasValidGender) {
      if (user.accountType === "player") {
        navigate("/complete-profile/birthday");
      } else {
        navigate("/complete-profile/documents");
      }
      return;
    }

    // Pre-select existing gender if available (but not "other" for non-organizations)
    const currentGender = user.gender || "";
    if (currentGender && currentGender !== 'other') {
      setSelectedGender(currentGender);
    }
  }, [user, navigate, updatePageTitle]);

  const handleGenderSubmit = async () => {
    if (!selectedGender) {
      setErrors({ gender: t("messages.pleaseSelectYourGender") });
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const token = localStorage.getItem("token");

      const response = await fetch(`${API_URL}/user/set-gender`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ gender: selectedGender })
      });

      if (!response.ok) {
        throw new Error(t("messages.failedToUpdateGender"));
      }

      // Update user data - handle both possible locations for gender
      const updatedUser = { 
        ...user, 
        gender: selectedGender
      };
      updateUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));

      // Navigate to next step based on account type
      if (user.accountType === "player") {
        navigate("/complete-profile/birthday");
      } else {
        navigate("/complete-profile/documents");
      }

    } catch (error) {
      setErrors({ gender: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Progress Indicator */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center bg-white rounded-full px-6 py-3 shadow-lg">
            <div className="flex items-center space-x-2 opacity-75">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">✓</div>
              <span className="text-gray-600 font-medium">Account Type</span>
            </div>
            <div className="mx-4 w-8 h-0.5 bg-green-500"></div>
            <div className="flex items-center space-x-2 opacity-75">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">✓</div>
              <span className="text-gray-600 font-medium">Phone</span>
            </div>
            <div className="mx-4 w-8 h-0.5 bg-green-500"></div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-[#F15213] rounded-full flex items-center justify-center text-white text-sm font-bold">3</div>
              <span className="text-gray-600 font-medium">Gender</span>
            </div>
            <div className="mx-4 w-8 h-0.5 bg-gray-200"></div>
            <div className="flex items-center space-x-2 opacity-50">
              <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-gray-400 text-sm font-bold">4</div>
              <span className="text-gray-400 font-medium">Documents</span>
            </div>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-[#F15213] rounded-full flex items-center justify-center mx-auto mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" className="w-10 h-10 text-white">
                <path fill="currentColor" d="M144 0a80 80 0 1 1 0 160A80 80 0 1 1 144 0zM512 0a80 80 0 1 1 0 160A80 80 0 1 1 512 0zM0 298.7C0 239.8 47.8 192 106.7 192h42.7c15.9 0 31 3.5 44.6 9.7c-1.3 7.2-1.9 14.7-1.9 22.3c0 38.2 16.8 72.5 43.3 96c-.2 0-.4 0-.7 0H21.3C9.6 320 0 310.4 0 298.7zM405.3 320c-.2 0-.4 0-.7 0c26.6-23.5 43.3-57.8 43.3-96c0-7.6-.7-15-1.9-22.3c13.6-6.3 28.7-9.7 44.6-9.7h42.7C592.2 192 640 239.8 640 298.7c0 11.8-9.6 21.3-21.3 21.3H405.3zM224 224a96 96 0 1 1 192 0 96 96 0 1 1 -192 0zM128 485.3C128 411.7 187.7 352 261.3 352H378.7C452.3 352 512 411.7 512 485.3c0 14.7-11.9 26.7-26.7 26.7H154.7c-14.7 0-26.7-11.9-26.7-26.7z"/>
              </svg>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#F15213] mb-4">
              Select Your Gender
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              This helps us personalize your experience
            </p>
          </div>

          {errors.gender && (
            <div className="bg-red-50 border-l-4 border-red-400 text-red-700 px-6 py-4 rounded-lg mb-8">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm">{errors.gender}</p>
                </div>
              </div>
            </div>
          )}

          {/* Gender Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12 max-w-2xl mx-auto">
            {genderOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setSelectedGender(option.value)}
                className={`relative p-8 rounded-3xl border-3 transition-all duration-300 transform hover:scale-105 ${
                  selectedGender === option.value
                    ? 'border-[#F15213] bg-orange-50 text-[#F15213] shadow-2xl'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-xl'
                }`}
              >
                <div className="text-center">
                  <div className="text-6xl mb-4">{option.icon}</div>
                  <h3 className={`text-2xl font-bold ${
                    selectedGender === option.value ? 'text-[#F15213]' : 'text-gray-800'
                  }`}>
                    {option.label}
                  </h3>
                </div>
                
                {selectedGender === option.value && (
                  <div className="absolute top-4 right-4">
                    <div className="w-8 h-8 bg-[#F15213] rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                )}
              </button>
            ))}
          </div>

          <div className="text-center">
            <Btn 
              onClick={handleGenderSubmit} 
              disabled={isLoading || !selectedGender}
              className="px-12 py-4 text-xl font-semibold bg-[#F15213] hover:bg-orange-600 transform hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </div>
              ) : (
                <>
                  Continue
                  <svg className="ml-2 w-5 h-5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </>
              )}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
} 